import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Award, CalendarDays, CalendarPlus, Check, Clock, FileQuestion, FileText, Flag, ListChecks, Lock, MessageSquareQuote,
  MonitorCheck, NotebookPen, Repeat, Target, Ticket, Timer,
} from 'lucide-react';
import { apiErrorMessage, trailService } from '../services/api';
import {
  Breadcrumbs, Button, EmptyState, Field, GlobalShell, Loader, Modal, ScoreCard, SectionTitle,
} from '../components/ui';
import { AvailabilityModal } from '../components/trails/TrailBits';
import { fmtHours, isoDate, trailCrumbs } from '../utils/trails';
import './TrailExam.css';

const TIMELINE_ICONS = { monitor: MonitorCheck, clock: Clock, timer: Timer, award: Award };

// A reta final só traz os títulos dos recursos do dia: o ícone sai do nome (simulado, caderno de erros ou material)
const FINAL_ICONS = { exam: Target, errors: NotebookPen, doc: FileText };
const finalKind = (title) => (/caderno de erros|\berros?\b/i.test(title) ? 'errors' : /simulad/i.test(title) ? 'exam' : 'doc');

/** Dia da prova: placar (contagem, ritmo e números), o dia em ordem, checklist, relatos e reta final. */
function TrailExam() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [rescheduling, setRescheduling] = useState(false);
  const [enrolling, setEnrolling] = useState(false);

  const load = useCallback(() => {
    trailService.exam(slug).then(setData).catch(err => setError(apiErrorMessage(err, 'Não foi possível abrir o dia da prova.')));
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (item) => {
    const done = !item.done;
    setData(d => ({ ...d, checklist: d.checklist.map(x => (x.id === item.id ? { ...x, done } : x)) }));
    try {
      await trailService.checklist(slug, item.id, done);
    } catch {
      setData(d => ({ ...d, checklist: d.checklist.map(x => (x.id === item.id ? { ...x, done: !done } : x)) }));
    }
  };

  if (error) {
    return <GlobalShell><EmptyState file="prova.xml" title="Não foi possível abrir o dia da prova" description={error} action={<Button onClick={() => navigate(`/trilhas/${slug}`)}>Ver a trilha</Button>} /></GlobalShell>;
  }
  if (!data) return <GlobalShell><Loader label="Carregando..." /></GlobalShell>;

  const checked = data.checklist.filter(c => c.done).length;
  const hasDate = data.daysLeft != null && data.daysLeft >= 0;
  const stats = [
    data.questions != null && { icon: FileQuestion, value: data.questions, label: 'questões' },
    data.minutes != null && { icon: Clock, value: data.minutes, label: 'minutos' },
    data.passPct != null && {
      icon: Target, value: `${data.passPct}%`, label: 'nota de corte', tone: 'lime',
      hint: data.passQuestions ? `· ${data.passQuestions} acertos` : undefined,
    },
    data.attempts != null && { icon: Ticket, value: data.attempts, label: 'tentativas', hint: 'no voucher' },
  ].filter(Boolean);
  // Sem inscrição, marcar a data passa por "Começar trilha" (a data vai junto com a disponibilidade)
  const markDate = () => (data.enrolled ? setRescheduling(true) : setEnrolling(true));
  const finalRange = data.finalDays.length > 0
    ? `Reta final · dias ${data.finalDays[0].n} a ${data.finalDays[data.finalDays.length - 1].n}`
    : 'Reta final';

  return (
    <GlobalShell trail={data}>
      <div className="ss-page ss-page--wide ss-page--crumbs tre-page">
        <Breadcrumbs items={[
          ...trailCrumbs(navigate, data),
          { label: 'Dia da prova' },
        ]} />

        <header className="tre-head">
          <span className="tre-head__marker" aria-hidden="true"><Flag size={21} /></span>
          <div className="tre-head__text">
            {data.examCode && <code className="tre-head__code">{data.examCode}</code>}
            <h1 className="tre-head__title">Dia da prova</h1>
            {data.examMode && <span className="tre-head__sub">{data.examMode}</span>}
          </div>
        </header>

        <ScoreCard
          tour="dia-prova"
          inline
          className="tre-score"
          top={(
            <div className="tre-band">
              <div className="tre-band__count">
                <span className="tre-eyebrow">{hasDate ? (data.daysLeft === 0 ? 'É hoje' : 'Faltam') : 'Contagem'}</span>
                {hasDate ? (
                  <span className="tre-band__num">
                    {data.daysLeft}<span>{data.daysLeft === 1 ? 'dia' : 'dias'}</span>
                  </span>
                ) : (
                  <span className="tre-band__nodate">Data não marcada</span>
                )}
                {hasDate && data.dateLabel && <span className="tre-band__note">{data.dateLabel} · {data.examTime}</span>}
                {data.finalStart && <span className="tre-band__note">A reta final começa em {data.finalStart}.</span>}
                <Button variant={hasDate ? 'outline' : 'accent'} size="sm" icon={hasDate ? CalendarDays : CalendarPlus} onClick={markDate}
                        className="tre-band__cta">
                  {hasDate ? 'Reagendar' : 'Marcar data da prova'}
                </Button>
              </div>
              {data.pace && (
                <div className="tre-band__pace">
                  <span className="tre-band__paceicon"><Timer size={22} aria-hidden="true" /></span>
                  <span className="tre-band__pacetext">
                    <span className="tre-eyebrow">Ritmo</span>
                    <strong>{data.pace} <span>por questão</span></strong>
                    <span>Na metade do tempo ({data.halfTime}), mire em ter passado da questão {data.halfQuestion}.</span>
                  </span>
                </div>
              )}
            </div>
          )}
          stripe
          items={stats}
        />

        {data.timeline.length > 0 && (
          <section className="ss-section tre-section">
            <SectionTitle icon={Clock}>No dia</SectionTitle>
            <ol className="tre-day" style={{ '--tre-cols': Math.min(data.timeline.length, 4) }}>
              {data.timeline.map((t, i) => {
                const Icon = TIMELINE_ICONS[t.icon] || Clock;
                return (
                  <li key={t.title} className="tre-day__card">
                    <div className="tre-day__head">
                      <span className="tre-day__icon"><Icon size={15} aria-hidden="true" /></span>
                      <span>
                        <span className="tre-day__n">{String(i + 1).padStart(2, '0')}</span>
                        <strong>{t.title}</strong>
                      </span>
                    </div>
                    {(t.lines || []).length > 0 && (
                      <ul className="tre-day__lines">
                        {t.lines.map(l => <li key={l}>{l}</li>)}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ol>
          </section>
        )}

        {(data.checklist.length > 0 || data.reports.length > 0) && (
          <div className="tre-grid ss-section">
            {data.checklist.length > 0 && (
              <section className="tre-col">
                <SectionTitle icon={ListChecks} aside={`${checked} de ${data.checklist.length}`}>Checklist do ambiente</SectionTitle>
                <div className="tre-card">
                  {(!data.enrolled || data.checklist.some(c => c.report)) && (
                    <div className="tre-check__bar">
                      {!data.enrolled && <span><Lock size={13} aria-hidden="true" />Comece a trilha para marcar o checklist.</span>}
                      {data.checklist.some(c => c.report) && (
                        <span className="tre-check__legend"><MessageSquareQuote size={12} aria-hidden="true" />= vem de relato</span>
                      )}
                    </div>
                  )}
                  <ul className="tre-check">
                    {data.checklist.map(c => (
                      <li key={c.id} className={`${c.done ? 'is-done' : ''} ${c.critical ? 'is-critical' : ''}`}>
                        <button type="button" className={`tre-box ${!data.enrolled ? 'is-off' : ''}`} aria-pressed={c.done} onClick={() => toggle(c)}
                                disabled={!data.enrolled} aria-label={c.done ? `Desmarcar ${c.title}` : `Marcar ${c.title}`}>
                          {c.done && <Check size={13} strokeWidth={3.2} />}
                        </button>
                        <span className="tre-check__text">
                          <strong>{c.title}{c.critical && <span className="tre-forbid">Proibido</span>}</strong>
                          {c.text && <span>{c.text}</span>}
                        </span>
                        {c.report && (
                          <span className="tre-check__rel" role="img" aria-label="Vem de relato" title="Vem de relato">
                            <MessageSquareQuote size={14} aria-hidden="true" />
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            )}

            {data.reports.length > 0 && (
              <aside className="tre-col">
                <SectionTitle icon={MessageSquareQuote} iconTone="warning"
                              aside={`${data.reports.length} ${data.reports.length === 1 ? 'relato' : 'relatos'}`}>
                  Quem já fez
                </SectionTitle>
                <div className="tre-reports">
                  {data.reports.map(r => (
                    <blockquote key={r} className="tre-report">
                      <span className="tre-report__quote" aria-hidden="true">“</span>
                      <p>{r}</p>
                    </blockquote>
                  ))}
                </div>
              </aside>
            )}
          </div>
        )}

        {data.finalDays.length > 0 && (
          <section className="ss-section tre-section">
            <SectionTitle icon={Repeat} aside={`${fmtHours(data.finalMinutes)} · antes da prova`}>{finalRange}</SectionTitle>
            <div className="tre-final">
              {data.finalDays.map(d => (
                <div key={d.n} className="tre-final__card">
                  <div className="tre-final__head">
                    <span className="tre-final__day">Dia {d.n} <span>{d.date}</span></span>
                    <strong className="tre-final__hours">{fmtHours(d.minutes)}</strong>
                  </div>
                  <ul className="tre-final__items">
                    {String(d.title || '').split(' + ').filter(Boolean).map(t => {
                      const k = finalKind(t);
                      const Icon = FINAL_ICONS[k];
                      return <li key={t} className={`is-${k}`}><Icon size={13} aria-hidden="true" />{t}</li>;
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      <RescheduleModal open={rescheduling} data={data} slug={slug} onClose={() => setRescheduling(false)} onDone={() => { setRescheduling(false); load(); }} />
      <AvailabilityModal
        open={enrolling}
        mode="enroll"
        trail={data}
        onClose={() => setEnrolling(false)}
        onDone={() => { setEnrolling(false); load(); }}
      />
    </GlobalShell>
  );
}

function RescheduleModal({ open, data, slug, onClose, onDone }) {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('09:00');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDate(data.examDate || '');
      setTime(data.examTime || '09:00');
      setError(null);
    }
  }, [open, data]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await trailService.updateEnrollment(slug, { examDate: date || null, examTime: time || null });
      onDone();
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível salvar a data.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={data.examDate ? 'Reagendar a prova' : 'Marcar a data da prova'}
      footer={(
        <>
          {data.examDate && <Button variant="ghost" onClick={() => { setDate(''); }}>Tirar a data</Button>}
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving}>Salvar</Button>
        </>
      )}
    >
      <div className="tre-resched">
        <Field label="Data"><input type="date" value={date} min={isoDate(1)} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field label="Horário"><input type="time" value={time} onChange={(e) => setTime(e.target.value)} /></Field>
      </div>
      <p className="tre-resched__note">O plano continua com as mesmas horas por dia. Se não couber até a prova, o plano avisa quantas horas faltam.</p>
      {error && <p className="tr-error" role="alert">{error}</p>}
    </Modal>
  );
}

export default TrailExam;
